import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Box, Button, Stack, Typography, styled } from '@mui/material';

import { useLocation } from 'react-router';
import { useReactToPrint } from 'react-to-print';
import { Doc } from 'src/@types/docs';

import { EditorStylesBase } from '@modules/editor';
import { Note } from 'src/@types/notes-transcription';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { AiTranscription } from 'src/features/ActionsBar/Transcriptions';
import { useTranscriptionQuery } from 'src/features/ActionsBar/Transcriptions/RightDrawer/useTranscriptionQuery';
import { useNotesTranscriptionModal } from 'src/features/ActionsBar/Transcriptions/TranscriptionModal';
import { PastSessionItemHeader } from 'src/features/GatheringSessions/PastSessionItemHeader';
import { useGatheringSessionByIdQuery } from 'src/features/GatheringSessions/useGatheringSessionByIdQuery';
import { UserRecentFeature } from 'src/generated/graphql';
import { useLocales } from 'src/locales';
import { useUserRecentsPersistance } from 'src/sections/@dashboard/recents/shared/useUserRecentsPersistance';
import { useDocsUIState } from 'src/state/ui/useDocsUIState';
import { createGlobalStyle } from 'styled-components';
import { useUpdateDocMutation } from '../shared/useUpdateDocMutation';
import DocContainer, { pageStyle } from './DocContainer';
import {
  DocContentContextProvider,
  useDocContentContext,
} from './DocContentContext';
import { DocContentEditor } from './DocContentEditor';
import { DocContentNav } from './DocContentNav';
import { DocCover } from './DocCover';
import { DocFooter } from './DocFooter';
import { DocTitle } from './DocTitle';
import { Header } from './Header';

type Props = {
  doc: Doc;
};

const EmptyArea = styled(Box)(() => ({
  minHeight: '30vh',
  width: '100%',
}));

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  height: 100%;
`;

const StyledTranscriptionMembersStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  paddingLeft: theme.spacing(8),
  alignItems: 'center',
  flexGrow: 1,
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
  },
}));

const GlobalStyle = createGlobalStyle`
  @media print {
    .no-print {
      display: none !important;
    }
  }
`;

export function DocContentInner() {
  const { doc, editor, canRender } = useDocContentContext();
  const { addUserRecent } = useUserRecentsPersistance();
  const { updateDoc } = useUpdateDocMutation();
  const { translate } = useLocales();
  const location = useLocation();
  const { docsFullWidth, docScrollHeight, setDocScrollHeight, docsExpanded } =
    useDocsUIState(doc._id);

  const [initialContent, setInitialContent] = useState('');

  const printRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const [openedTranscription, setOpenedTranscription] = useState(false);

  const { showNotesTranscriptionModal } = useNotesTranscriptionModal();

  const { transcription } = useTranscriptionQuery({
    transcriptionId: doc.noteRef as string,
    noteId: doc._id,
  });

  const { session } = useGatheringSessionByIdQuery({
    sessionId: (doc as Note).channelSession,
  });

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle,
  });

  const addDocUserRecent = useCallback(
    () =>
      addUserRecent({
        title: doc?.title,
        objectRef: doc?._id,
        icon: doc?.icon?.value,
        feature: UserRecentFeature.Docs,
        url: PATH_DASHBOARD.docs.view(doc?._id || ''),
      }),
    [addUserRecent, doc],
  );

  useLayoutEffect(() => {
    if (docScrollHeight && canRender) {
      stackRef.current?.scrollTo(0, docScrollHeight);
    }
  }, [canRender, docScrollHeight]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: not putting setDocScrollHeight on dependencies to not get a loop
  useEffect(() => {
    const container = stackRef.current;
    if (container?.scrollTop) {
      setDocScrollHeight(container?.scrollTop);
    }
  }, [location]);

  const handleAddToEditor = (text: string) => {
    editor
      .chain()
      .focus('end')
      .splitBlock()
      .insertMarkdown(text, {
        inlineSingleParagraph: true,
      })
      .run();
  };

  const handleOpenTranscription = () => {
    if (!transcription) return;
    const note = doc as Note;
    showNotesTranscriptionModal({
      transcription,
      channelId: note.channelRef as string,
      noteId: note._id,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (editor && stackRef.current) {
      const tocExtension = editor.extensionManager.extensions.find(
        (ext) => ext.name === 'tableOfContents',
      );

      if (tocExtension) {
        tocExtension.options.scrollParent = stackRef.current;
      }

      setInitialContent(editor.getHTML());
    }
  }, [editor, stackRef.current]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!!doc && !!editor && !!initialContent) {
      return () => {
        if (editor.getHTML() !== initialContent) {
          addDocUserRecent();
        }
      };
    }
  }, [doc, editor, initialContent]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      openedTranscription ||
      !doc?.noteRef ||
      doc?.speakersSelected ||
      !transcription
    )
      return;

    setOpenedTranscription(true);
    handleOpenTranscription();
  }, [openedTranscription, doc, transcription]);

  if (!doc || !editor || !canRender) return null;

  return (
    <StyledContainer>
      <Header doc={doc} handlePrint={handlePrint} />
      <GlobalStyle />
      {session && <PastSessionItemHeader session={session} />}
      {doc.noteRef && (
        <AiTranscription note={doc} onAddToEditor={handleAddToEditor} />
      )}
      <EditorStylesBase ref={printRef} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Stack
          direction="row"
          sx={{
            height: '100%',
            overflow: 'hidden',
            maxHeight: '100%',
            position: 'relative',
          }}
        >
          <DocContentNav />

          <Stack
            ref={stackRef}
            sx={{ width: '100%', height: '100%', overflowY: 'auto' }}
          >
            {doc.cover && <DocCover doc={doc} />}

            {!doc.noteRef || doc?.speakersSelected ? (
              <>
                <DocContainer
                  isFullWidth={docsFullWidth}
                  isContentExpanded={docsExpanded}
                >
                  <DocTitle doc={doc} updateDoc={updateDoc} />
                  {editor ? <DocContentEditor editor={editor} /> : null}
                  {doc.showFooter && <DocFooter doc={doc} />}
                </DocContainer>

                <EmptyArea onClick={() => editor.commands.focus()} />
              </>
            ) : (
              <StyledTranscriptionMembersStack>
                <Typography variant="subtitle2">
                  {translate(
                    'collaborate.docs.you_need_to_select_transcription_members_text',
                  )}
                </Typography>
                <Button variant="contained" onClick={handleOpenTranscription}>
                  {translate('collaborate.docs.select_transcription_members')}
                </Button>
              </StyledTranscriptionMembersStack>
            )}
          </Stack>
        </Stack>
      </EditorStylesBase>
    </StyledContainer>
  );
}

export const DocContent = ({ doc }: Props) => {
  return (
    <DocContentContextProvider doc={doc}>
      <DocContentInner />
    </DocContentContextProvider>
  );
};
